import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import {
    IntelligenceStats,
    DetectedPattern,
    PatternDetailData,
    PaymentIntelligence,
    RiskRecommendations,
    RevenueAnalysis,
    OrgSummary,
    RecommendationFeedbackRequest,
} from '../types';

// Intelligence Stats (Dashboard Overview)
export function useIntelligenceStats() {
    return useQuery({
        queryKey: ['intelligence', 'stats'],
        queryFn: async () => {
            const response = await api.get('/intelligence/stats');
            return response.data.data as IntelligenceStats;
        },
        staleTime: 60 * 1000, // 1 minute
        refetchInterval: 5 * 60 * 1000, // 5 minutes
        retry: false, // Don't retry on 403 (feature not available)
    });
}

// Patterns List
export function usePatterns(page = 1, perPage = 20) {
    return useQuery({
        queryKey: ['intelligence', 'patterns', page, perPage],
        queryFn: async () => {
            const response = await api.get('/intelligence/patterns', {
                params: { page, per_page: perPage }
            });
            return response.data as {
                success: boolean;
                data: DetectedPattern[];
                meta: {
                    current_page: number;
                    last_page: number;
                    total: number;
                };
            };
        },
    });
}

// Pattern Detail with AI Insight
export function usePatternDetail(patternId: number | null) {
    return useQuery({
        queryKey: ['intelligence', 'patterns', patternId],
        queryFn: async () => {
            const response = await api.get(`/intelligence/patterns/${patternId}`);
            return response.data.data as PatternDetailData;
        },
        enabled: !!patternId,
    });
}

// Acknowledge Pattern
export function useAcknowledgePattern() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (patternId: number) => {
            const response = await api.post(`/intelligence/patterns/${patternId}/acknowledge`);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['intelligence', 'patterns'] });
            queryClient.invalidateQueries({ queryKey: ['intelligence', 'stats'] });
        },
    });
}

// Resolve Pattern
export function useResolvePattern() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ patternId, note }: { patternId: number; note?: string }) => {
            const response = await api.post(`/intelligence/patterns/${patternId}/resolve`, { note });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['intelligence', 'patterns'] });
            queryClient.invalidateQueries({ queryKey: ['intelligence', 'stats'] });
        },
    });
}

// Payment Intelligence (Loss Probability)
export function usePaymentIntelligence(paymentIntentId: string | null) {
    return useQuery({
        queryKey: ['intelligence', 'payment', paymentIntentId],
        queryFn: async () => {
            const response = await api.get(`/intelligence/payment/${paymentIntentId}`);
            return response.data.data as PaymentIntelligence;
        },
        enabled: !!paymentIntentId,
        retry: false, // Don't retry if intelligence not ready yet
    });
}

// Risk AI Recommendations
export function useRiskRecommendations(riskId: number | null, shouldFetch = true) {
    return useQuery({
        queryKey: ['intelligence', 'recommendations', riskId],
        queryFn: async () => {
            const response = await api.get(`/intelligence/risks/${riskId}/recommendations`);
            return response.data.data as RiskRecommendations;
        },
        enabled: !!riskId && shouldFetch,
        retry: false, // Don't retry on 403 (feature not available)
    });
}

// Revenue Analysis
export function useRevenueAnalysis(days = 30) {
    return useQuery({
        queryKey: ['intelligence', 'revenue-analysis', days],
        queryFn: async () => {
            const response = await api.get('/intelligence/revenue-analysis', {
                params: { days }
            });
            return response.data.data as RevenueAnalysis;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// Organization AI Summary
export function useOrgSummary() {
    return useQuery({
        queryKey: ['intelligence', 'org-summary'],
        queryFn: async () => {
            const response = await api.get('/intelligence/org-summary');
            return response.data.data as OrgSummary;
        },
        staleTime: 30 * 60 * 1000, // 30 minutes (AI summary is expensive)
        retry: false, // Don't retry on 403 (feature not available)
    });
}

// Submit Recommendation Feedback
export function useRecommendationFeedback() {
    return useMutation({
        mutationFn: async ({ recommendationId, feedback }: {
            recommendationId: number;
            feedback: RecommendationFeedbackRequest
        }) => {
            const response = await api.post(
                `/intelligence/recommendations/${recommendationId}/feedback`,
                feedback
            );
            return response.data;
        },
    });
}
